import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/mantine/style.css';

function useIsDark() {
  const [isDark, setIsDark] = useState(() => 
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDark(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

interface Props {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
}

export interface RichTextEditorHandle {
  undo: () => void;
  redo: () => void;
}

export const RichTextEditor = forwardRef<RichTextEditorHandle, Props>(
  ({ content, onChange, placeholder = "Enter text or type '/' for commands" }, ref) => {
    const isDark = useIsDark();

    const editor = useCreateBlockNote({
      domAttributes: {
        editor: {
          class: 'notion-editor',
        },
      },
    });

    useImperativeHandle(ref, () => ({
      undo: () => editor.undo(),
      redo: () => editor.redo(),
    }), [editor]);

    useEffect(() => {
      if (content && editor) {
        try {
          const parsed = JSON.parse(content);
          if (Array.isArray(parsed) && parsed.length > 0) {
            editor.replaceBlocks(editor.document, parsed);
          }
        } catch {
          if (content.trim()) {
            const tryLoadHTML = async () => {
              try {
                const blocks = await editor.tryParseHTMLToBlocks(content);
                if (blocks.length > 0) {
                  editor.replaceBlocks(editor.document, blocks);
                }
              } catch {}
            };
            tryLoadHTML();
          }
        }
      }
    }, []);

    const handleChange = useCallback(() => {
      const blocks = editor.document;
      onChange(JSON.stringify(blocks));
    }, [editor, onChange]);

    return (
      <div className="flex-1 rounded-lg border border-border overflow-y-auto bg-background notion-editor-wrapper">
        <BlockNoteView
          editor={editor}
          onChange={handleChange}
          theme={isDark ? 'dark' : 'light'}
        />
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';

